import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'https://dev-app2.arth.team',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});


api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
    }
    return Promise.reject({
      status: error.response?.status,
      message: error.message,
      data: error.response?.data,
    });
  }
);

const crud = (base: string) => ({
  list: (params?: any) => api.get(base, { params }),
  get: (id: number | string) => api.get(`${base}/${id}`),
  create: (data: any) => api.post(base, data),
  update: (id: number | string, data: any) =>
    api.put(`${base}/${id}`, data),
  delete: (id: number | string) => api.delete(`${base}/${id}`),
});


export const API = {
  
  companies: {
    list: (params?: any) => api.get('/api/v1/company', { params }),
    get: (id: number | string) => api.get(`/api/v1/company/${id}`),
    create: (data: any) => api.post('/api/v1/company', data),
    update: (id: number | string, data: any) =>
      api.put(`/api/v1/company/${id}`, data),
    delete: (id: number | string) => api.put(`/api/v1/company/deactivate/${id}`),
    deactivate: (id: number) =>
      api.put(`/api/v1/company/deactivate/${id}`),
  },

  projects: {
    list: (params?: any) => api.get('/api/v1/project', { params }),
    get: (id: number | string) => api.get(`/api/v1/project/${id}`),
    create: (data: any) => api.post('/api/v1/project', data),
    update: (id: number | string, data: any) =>
      api.put(`/api/v1/project/${id}`, data),
    delete: (id: number | string) => api.put(`/api/v1/project/deactivate/${id}`),
    deactivate: (id: number) =>
      api.put(`/api/v1/project/deactivate/${id}`),
  },

  users: {
    ...crud('/api/v1/user'),
  },

  units: {
    ...crud('/api/v1/unit'),
    deactivate: (id: number) =>
      api.put(`/api/v1/unit/deactivate/${id}`),
  },

  equipment: {
    ...crud('/api/v1/equipment'),
    deactivate: (id: number) =>
      api.put(`/api/v1/equipment/delete/${id}`),
  },

  equipmentSpecs: {
    ...crud('/api/v1/equipmentspec'),
    deactivate: (id: number) =>
      api.put(`/api/v1/equipmentspec/delete/${id}`),
  },

  iotDevices: {
    ...crud('/api/v1/iotdevices'),
    deactivate: (id: number) =>
      api.put(`/api/v1/iotdevices/delete/${id}`),

    heartbeat: (id: number) =>
      api.post(`/api/v1/iotdevices/${id}/heartbeat`),
  },

  sensorData: {
    ...crud('/api/v1/iotsensordata'),
  },

  /* ------------------ Alert Rules ------------------ */
  alertRules: {
    ...crud('/api/v1/iotalertrule'),
    deactivate: (id: number) =>
      api.put(`/api/v1/iotalertrule/deactivate/${id}`),
  },

  /* ------------------ Alert History ------------------ */
  alertHistory: {
    list: (params?: any) =>
      api.get('/api/v1/iotalerthistory', { params }),
    get: (id: number) =>
      api.get(`/api/v1/iotalerthistory/${id}`),
    create: (data: any) =>
      api.post('/api/v1/iotalerthistory', data),
    update: (id: number, data: any) =>
      api.put(`/api/v1/iotalerthistory/${id}`, data),
    acknowledge: (id: number) =>
      api.put(`/api/v1/iotalerthistory/acknowledge/${id}`),
    resolve: (id: number, data: any) =>
      api.put(`/api/v1/iotalerthistory/resolve/${id}`, data),
  },

  /* ------------------ API Keys ------------------ */
  apiKeys: {
    list: (params?: any) => api.get('/api/v1/apikeys', { params }),
    get: (id: number | string) => api.get(`/api/v1/apikeys/${id}`),
    create: (data: any) => api.post('/api/v1/apikeys', data),
    update: (id: number | string, data: any) =>
      api.put(`/api/v1/apikeys/${id}`, data),
    delete: (id: number | string) => api.put(`/api/v1/apikeys/deactivate/${id}`),
    deactivate: (id: number) =>
      api.put(`/api/v1/apikeys/deactivate/${id}`),
  },

  /* ------------------ Models ------------------ */
  models: {
    ...crud('/api/v1/models'),
    getTree: (projectId: number) =>
      api.get(`/api/v1/models/tree/${projectId}`),
    deactivate: (id: number) =>
      api.put(`/api/v1/models/deactivate/${id}`),
    
    // Visualizations
    visualizations: {
      list: (modelId: number, params?: any) =>
        api.get(`/api/v1/models/${modelId}/visualizations`, { params }),
      create: (modelId: number, data: any) =>
        api.post(`/api/v1/models/${modelId}/visualizations`, data),
      update: (modelId: number, vizId: number, data: any) =>
        api.put(`/api/v1/models/${modelId}/visualizations/${vizId}`, data),
      delete: (modelId: number, vizId: number) =>
        api.delete(`/api/v1/models/${modelId}/visualizations/${vizId}`),
    },
    
    // Actions
    actions: {
      list: (modelId: number, params?: any) =>
        api.get(`/api/v1/models/${modelId}/actions`, { params }),
      create: (modelId: number, data: any) =>
        api.post(`/api/v1/models/${modelId}/actions`, data),
      update: (modelId: number, actionId: number, data: any) =>
        api.put(`/api/v1/models/${modelId}/actions/${actionId}`, data),
      delete: (modelId: number, actionId: number) =>
        api.delete(`/api/v1/models/${modelId}/actions/${actionId}`),
    },
  },

  scheduler: {
    jobs: {
      ...crud('/api/v1/scheduler/jobs'),
      runNow: (jobId: number) =>
        api.post(`/api/v1/scheduler/jobs/${jobId}/run`),
      pause: (jobId: number) =>
        api.put(`/api/v1/scheduler/jobs/${jobId}/pause`),
    },

    runs: {
      list: (params?: any) =>
        api.get('/api/v1/scheduler/runs', { params }),
      get: (id: number) =>
        api.get(`/api/v1/scheduler/runs/${id}`),
      create: (data: any) =>
        api.post('/api/v1/scheduler/runs', data),
    },
  },

  /* ------------------ External APIs ------------------ */
  externalApis: {
    ...crud('/api/v1/externalapi'),
    deactivate: (id: number) =>
      api.put(`/api/v1/externalapi/deactivate/${id}`),
  },
};

/* =========================================================
   DEFAULT EXPORT (optional)
========================================================= */
export default api;
